import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, Typography, Select, Row, Col, Checkbox, Modal } from "antd";
import { User, Mail, Phone, IdCard, LockKeyhole, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loginSuccess, signup } from "../store/slices/authSlice";
import useLocation from "../hooks/useLocation";
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const SignUp = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const [userAgreementModalVisible, setUserAgreementModalVisible] = useState(false);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [hasOpenedAgreement, setHasOpenedAgreement] = useState(false);

  const {
    countries,
    states,
    cities,
    selectedCountry,
    selectedState,
    selectedCity,
    setSelectedCountry,
    setSelectedState,
    setSelectedCity,
    loading: locationLoading,
  } = useLocation();

  // keep form.setFieldsValue effect so form picks up selection changes
  useEffect(() => {
    form.setFieldsValue({
      country: selectedCountry || undefined,
      state: selectedState || undefined,
      city: selectedCity || undefined,
    });
  }, [selectedCountry, selectedState, selectedCity, form]);


  const onFinish = async (values) => {
    try {
      // console.log("Form submitted:", values);
      const result = await dispatch(signup(values)).unwrap();
      // console.log("Signup successful:", result);
      if (result && (result.status === true || result.status === "success")) {
        toast.success(
          "Successfully Registered! Check your email for the verification code."
        );
        navigate("/verify-email", {
          replace: true,
          state: { email: values.email, phone: values.phone, fromSignup: true },
        });
      }
    } catch (error) {
      console.error("Signup failed:", error);
      toast.error(error.message || "Signup failed. Please try again.");
    }
  };

  const onFinishFailed = (errorInfo) => {
    // console.log("Failed:", errorInfo);
    toast.error(`Please check the form for ${errorInfo} and try again.`);
  };

  const handleUserAgreementAccept = () => {
    // mark as enabled (defensive)
    setHasOpenedAgreement(true);

    // set accepted
    form.setFieldsValue({ terms: true });
    // clear validation errors immediately
    form.validateFields(["terms"]).catch(() => {});
    setUserAgreementModalVisible(false);
  };

  const handleTermsAccept = () => {
    setHasOpenedAgreement(true);
    form.setFieldsValue({ terms: true });
    form.validateFields(["terms"]).catch(() => {});
    setTermsModalVisible(false);
  };

  // USER LICENSE ACCEPTANCE AGREEMENT
  const userAgreementContent = (
    <div style={{ maxHeight: "400px", overflowY: "auto", padding: "10px" }}>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        <strong>IMPORTANT-READ CAREFULLY:</strong> This User License Acceptance
        Agreement (“Agreement”) is entered into between <b>Inteliquant AI</b>{" "}
        (“Provider”) and the individual or entity accessing or using the
        software (“User”). By accessing, logging in, or using the Platform{" "}
        <b>InsightEdge AI / Control Edge AI</b> (“Software”), the User agrees to
        the terms below.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>1. GRANT OF LICENSE </strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider grants User a{" "}
        <b>non-exclusive, non-transferable, revocable license</b> to access and
        use the Software solely for User’s internal business purposes.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong> 2. Ownership</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        The Software, algorithms, models, datasets, interface, and documentation
        are owned exclusively by the Provider. This Agreement{" "}
        <b>does not transfer any intellectual property rights</b> to the User.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>3. Permitted Use</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>User may:</p>
      <ul
        style={{ marginLeft: "20px", marginBottom: "16px", lineHeight: "1.6" }}
      >
        <li>Upload, view, and analyse internal organizational data,</li>
        <li>Generate reports and insights based on Software outputs,</li>
        <li>
          Allow authorized employees to access the Software under User’s
          subscription plan.
        </li>
      </ul>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>4. Restrictions</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        User shall <b>not</b>:
      </p>
      <ul
        style={{ marginLeft: "20px", marginBottom: "16px", lineHeight: "1.6" }}
      >
        <li>
          Reverse-engineer, decompile, or copy the Software or underlying
          models,
        </li>
        <li>Sell, sublicense, rent, or provide access to any third party,</li>
        <li>Use the Software to build a competing product or model,</li>
        <li>Upload unlawful, malicious, or unauthorized data.</li>
      </ul>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>5. Confidentiality</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Both parties agree to keep each other’s confidential information secure
        and not disclose it to third parties except as required by law.
      </p>
      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>6. Data Handling </strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider processes data only for the purpose of running analytics and
        delivering results. Provider does{" "}
        <b>not claim ownership over User data.</b>
      </p>
      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>7. Support and Maintenance </strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider will offer updates and support as per the active subscription
        plan.
      </p>
      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>8. Termination</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider may suspend or terminate access if User violates this
        Agreement. Upon termination, the User’s access is revoked and stored
        data may be deleted after 30 days.
      </p>
      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong> 9. Disclaimer</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Software outputs are generated using statistical and machine learning
        models and may require human judgement. Provider shall not be liable for
        business decisions taken solely on Software output.
      </p>
      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>10. Governing Law</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        This Agreement is governed by the laws of the jurisdiction in which the
        Provider is located.{" "}
        <b>
          By clicking “Accept” the User confirms they have read and agree to
          this Agreement.
        </b>
      </p>
    </div>
  );

  // TERMS & CONDITIONS 
  const termsContent = (
    <div style={{ maxHeight: "400px", overflowY: "auto", padding: "10px" }}>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        These Terms & Conditions (“T&C”) govern use of the Platform provided by
        the Inteliquant AI.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>1. Definitions</strong>
      </h4>
      <ul
        style={{ marginLeft: "20px", marginBottom: "16px", lineHeight: "1.6" }}
      >
        <li>“Platform” refers to the SaaS application and its modules.</li>
        <li>
          “User” refers to any individual or entity authorized to use the
          Platform.
        </li>
        <li>
          “Subscription” refers to the purchased plan granting access to the
          Platform’s features.
        </li>
      </ul>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>2. Access & Subscription</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        User access is based on the subscription purchased. Unauthorized sharing
        of login credentials is prohibited.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
       <strong> 3. Data Security</strong>
      </h4>
      <ul
        style={{ marginLeft: "20px", marginBottom: "16px", lineHeight: "1.6" }}
      >
        <li>All data is encrypted during transit and storage.</li>
        <li>Provider implements industry-grade security measures.</li>
        <li>User is responsible for safeguarding login credentials.</li>
      </ul>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>4. Acceptable Use</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>User shall not:</p>
      <ul
        style={{ marginLeft: "20px", marginBottom: "16px", lineHeight: "1.6" }}
      >
        <li>Attempt to bypass platform security,</li>
        <li>Upload infected, harmful or unauthorized files,</li>
        <li>Use platform for illegal or unethical activities.</li>
      </ul>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>5. Data Privacy</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider does not sell or share User data with third parties. Data is
        processed only for analytics and insight generation.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>6. Output & Insights</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Platform produces statistical anomaly detection and insights. Final
        business decisions remain User’s responsibility.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>7. Payment & Renewals</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Subscriptions renew automatically unless cancelled before renewal date.
        Non-payment may lead to suspension of access.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>8. Service Availability</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider strives for <b>99% uptime</b>, excluding scheduled maintenance.
        Critical maintenance is notified in advance when possible.
      </p>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>9. Limitation of Liability</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider is not responsible for:
      </p>
      <ul
        style={{ marginLeft: "20px", marginBottom: "16px", lineHeight: "1.6" }}
      >
        <li>Losses arising from misuse of insights,</li>
        <li>System downtime due to internet or third-party dependency,</li>
        <li>User errors in uploading incorrect or unauthenticated data.</li>
      </ul>

      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>10. Termination</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider reserves the right to terminate access for T&C breach. User may
        terminate subscription at any time; refunds are subject to policy.
      </p>
      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}><strong>11. IP Rights</strong></h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Algorithms, models, UI, platform design and proprietary logic belong
        exclusively to the Provider.
      </p>
      <h4 style={{ marginTop: "20px", marginBottom: "12px" }}>
        <strong>12. Changes to T&C</strong>
      </h4>
      <p style={{ marginBottom: "16px", lineHeight: "1.6" }}>
        Provider may update these T&C; continued use of the Platform implies
        acceptance.
      </p>
    </div>
  );

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-2xl">
        <Card className="shadow-2xl border-0 rounded-2xl bg-[#141c31] backdrop-blur-sm p-2">
          <div className="text-center mb-6">
            <Title
              level={2}
              className="!text-white mb-1 font-serif text-2xl sm:text-3xl md:text-4xl"
            >
              Advanced Data Analysis & Anomaly Detection
            </Title>
            <Text className="text-white text-sm sm:text-base font-sans">
              Sign up to access your dashboard
            </Text>
          </div>

          <Form
            form={form}
            name="SignUp"
            initialValues={{ terms: false }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
          >
            <Row gutter={[12]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      First Name
                    </span>
                  }
                  name="firstName"
                  rules={[
                    { required: true, message: "Please input your name!" },
                    {
                      min: 2,
                      message: "Name must be at least 2 characters long!",
                    },
                  ]}
                >
                  <Input
                    prefix={<User className="text-gray-500" />}
                    placeholder="John"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Last Name
                    </span>
                  }
                  name="lastName"
                  rules={[
                    { required: true, message: "Please input your name!" },
                  ]}
                >
                  <Input
                    prefix={<User className="text-gray-500" />}
                    placeholder="Doe"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  className="mt-1"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Email
                    </span>
                  }
                  name="email"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address!",
                    },
                    { required: true, message: "Please input your email!" },
                  ]}
                >
                  <Input
                    prefix={<Mail className="text-gray-500" />}
                    placeholder="john.doe@company.com"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Phone
                    </span>
                  }
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Please input your phone number!",
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Phone number must be exactly 10 digits!",
                    },
                  ]}
                >
                  <Input
                    prefix={<Phone className="text-gray-500" />}
                    placeholder="9870000000"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                    maxLength={10}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="country"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          Country
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please select your country!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select your country"
                        size="large"
                        value={selectedCountry || undefined}
                        onChange={(val) => setSelectedCountry(val)}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {countries.map((c) => (
                          <Option key={c.name} value={c.name}>
                            {c.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="state"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          State
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your state!" },
                      ]}
                    >
                      <Select
                        placeholder="Select state"
                        size="large"
                        value={selectedState || undefined}
                        onChange={(val) => setSelectedState(val)}
                        disabled={!selectedCountry}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {states.map((s) => (
                          <Option key={s.iso2 || s} value={s.name || s}>
                            {s.name || s}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="city"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          City
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your city!" },
                      ]}
                    >
                      <Select
                        placeholder="Select city"
                        size="large"
                        value={selectedCity || undefined}
                        onChange={(val) => setSelectedCity(val)}
                        disabled={!selectedState}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {cities.map((ct, idx) => (
                          <Option
                            key={ct.id || `${ct.name || ct}-${idx}`}
                            value={ct.name || ct}
                          >
                            {ct.name || ct}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="domain"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Domain
                    </span>
                  }
                  rules={[{ required: true, message: "Please select domain!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your domain"
                    size="large"
                    className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                  >
                    <Option value="finance">Finance</Option>
                    <Option value="it">IT</Option>
                    <Option value="audit head">Audit Head</Option>
                    <Option value="cxo">CXO</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Organization
                    </span>
                  }
                  name="organization"
                  rules={[
                    {
                      required: true,
                      message: "Please input your organization!",
                    },
                    {
                      min: 2,
                      message:
                        "Organization must be at least 2 characters long!",
                    },
                  ]}
                >
                  <Input
                    prefix={<IdCard className="text-gray-500" />}
                    placeholder="Enter your organization name"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="password"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Password
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please input your password!" },
                    {
                      min: 8,
                      max: 15,
                      message:
                        "Password must be between 8 and 15 characters long!",
                    },
                    {
                      pattern:
                        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*]).+$/,
                      message:
                        "Password must contain at least 1 uppercase letters, 1 special characters, 1 numbers, and 1 lowercase letters!",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockKeyhole className="text-gray-500" />}
                    placeholder="Password"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="confirm"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Confirm Password
                    </span>
                  }
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "The two passwords that you entered do not match!"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockKeyhole className="text-gray-500" />}
                    placeholder="Confirm Password"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              {/* Terms and Conditions Checkbox */}
              <Col xs={24}>
                <Form.Item
                  name="terms"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "Please accept the terms and conditions"
                              )
                            ),
                    },
                  ]}
                >
                  <Checkbox
                    disabled={!hasOpenedAgreement}
                    checked={!!form.getFieldValue("terms")}
                    onChange={(e) => {
                      const alreadyAccepted = !!form.getFieldValue("terms");

                      // If already accepted, allow unchecking:
                      if (alreadyAccepted) {
                        form.setFieldsValue({ terms: e.target.checked });
                        form.validateFields(["terms"]).catch(() => {});
                        return;
                      }

                      // Not accepted yet — open the User Agreement modal (or Terms modal if you prefer)
                      // This ensures the user cannot simply tick the checkbox without accepting.
                      setUserAgreementModalVisible(true);
                      // ensure checkbox is enabled (it should already be if they clicked a link)
                      setHasOpenedAgreement(true);
                    }}
                    className={!hasOpenedAgreement ? "checkbox-disabled" : "text-gray-200"}
                  >
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHasOpenedAgreement(true); // enable the checkbox
                        setUserAgreementModalVisible(true); // open that modal
                      }}
                      className="text-blue-400 hover:text-blue-300 underline bg-transparent border-none cursor-pointer p-0"
                    >
                      User License Acceptance Agreement
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHasOpenedAgreement(true); // enable the checkbox
                        setTermsModalVisible(true); // open Terms modal
                      }}
                      className="text-blue-400 hover:text-blue-300 underline bg-transparent border-none cursor-pointer p-0"
                    >
                      Terms & Conditions
                    </button>
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item className="mb-0 pt-4">
                  {/* Sign Up Button and Login Link */}
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    <Button
                      htmlType="submit"
                      size="large"
                      disabled={loading}
                      className=" w-full !bg-gradient-to-r from-pink-600 to-purple-600 hover:!from-pink-700 hover:!to-purple-700 !text-white py-2 sm:py-3 text-sm sm:text-base transition-all duration-200 shadow-lg hover:!shadow-pink-500/25"
                    >
                      {loading ? "Creating Account..." : "SIGN UP"}
                    </Button>
                    <p className="text-white text-base">
                      Already Have account?{" "}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="font-semibold text-blue-500 hover:text-blue-400 transition-colors text-lg"
                    >
                      Login
                    </button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      </div>

      {/* USER LICENSE ACCEPTANCE AGREEMENT Modal */}
      <Modal
        title={
          <span style={{ color: "#4db8b8", fontSize: "20px" }}>
            USER LICENSE ACCEPTANCE AGREEMENT
          </span>
        }
        open={userAgreementModalVisible}
        onCancel={() => setUserAgreementModalVisible(false)}
        footer={[
          <Button
            key="decline"
            onClick={() => setUserAgreementModalVisible(false)}
            style={{
              backgroundColor: "#9ca3af",
              color: "white",
              border: "none",
            }}
          >
            Decline
          </Button>,
          <Button
            key="accept"
            type="primary"
            onClick={handleUserAgreementAccept}
            style={{
              backgroundColor: "#4db8b8",
              borderColor: "#4db8b8",
            }}
          >
            Accept
          </Button>,
        ]}
        width={700}
      >
        {userAgreementContent}
      </Modal>

      {/* TERMS & CONDITIONS Modal */}
      <Modal
        title={
          <span style={{ color: "#4db8b8", fontSize: "20px" }}>
            TERMS & CONDITIONS
          </span>
        }
        open={termsModalVisible}
        onCancel={() => setTermsModalVisible(false)}
        footer={[
          <Button
            key="decline"
            onClick={() => setTermsModalVisible(false)}
            style={{
              backgroundColor: "#9ca3af",
              color: "white",
              border: "none",
            }}
          >
            Decline
          </Button>,
          <Button
            key="accept"
            type="primary"
            onClick={handleTermsAccept}
            style={{
              backgroundColor: "#4db8b8",
              borderColor: "#4db8b8",
            }}
          >
            Accept
          </Button>,
        ]}
        width={700}
      >
        {termsContent}
      </Modal>
    </div>
  );
};

export default SignUp;